import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ActivateEmployeeModal } from "../ActivateFromUser";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Employees";
import {
  setStatusEmployees,
  getShowEmployees,
  deleteEmployeesData,
} from "@/Api/employees";
import { Badge } from "@/components/ui/badge";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { PositionHistorySection } from "../PositionHistorySection";
import { SalaryHistorySection } from "../SalaryHistorySection";
import { VacationSection } from "../VacationSection";
import {
  GenderLabel,
  MaritalStatusLabel,
  EthnicityLabel,
  EducationLevelLabel,
  PaymentFrequencyLabel,
} from "@/types/employeeFicha";

const formatDate = (value?: string | null) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "Não informado";

const UserTypeLabel: Record<number, string> = {
  1: "Administrador",
  2: "Usuário do Sistema",
  3: "Vendedor",
  4: "Usuário da Unidade",
  5: "Estoquista",
  6: "Somente ponto (RH)",
};

const InfoCell = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="rounded-md border bg-zinc-50 px-3 py-2 dark:bg-[#2c2c2c]">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm font-medium break-words">{value || "Não informado"}</div>
  </div>
);

const InfoSection = ({
  title,
  columns = 2,
  children,
}: {
  title: string;
  columns?: 2 | 3;
  children: React.ReactNode;
}) => (
  <div>
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </h4>
    <div
      className={`grid grid-cols-1 gap-3 ${
        columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"
      }`}
    >
      {children}
    </div>
  </div>
);

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();
  const [activating, setActivating] = useState<{
    uuid: string;
    name: string;
  } | null>(null);
  return (
    <>
      <ActivateEmployeeModal
        open={!!activating}
        onOpenChange={(open) => !open && setActivating(null)}
        user={activating}
        onActivated={(employee) =>
          updateFront((prev) => ({
            ...prev,
            data: prev?.data?.map((row) =>
              row?.user?.uuid === activating?.uuid
                ? { ...row, ...employee, user: row.user, has_employee: true }
                : row
            ),
          }))
        }
      />
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Unidade</TableHead>
            <TableHead>Setor</TableHead>
            <TableHead>Cargo</TableHead>
            <TableHead>Horário de Trabalho</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item, index) => {
            return (
              <TableRow key={item?.uuid ?? `user-${item?.user_id}`}>
                <TableCell className="truncate max-w-80">
                  {item?.user?.name}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.user?.email}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.has_employee === false
                    ? "Sem cadastro de colaborador"
                    : item?.unity?.name}
                </TableCell>
                <TableCell className="truncate max-w-60">
                  {item?.has_employee === false ? "-" : item?.sector?.name || "-"}
                </TableCell>
                <TableCell className="truncate max-w-60">
                  {item?.has_employee === false ? "-" : item?.position?.name || "-"}
                </TableCell>
                <TableCell className="truncate max-w-60">
                  {item?.has_employee === false ? "-" : item?.work_schedule?.name || "-"}
                </TableCell>

                {item?.has_employee === false ? (
                  <TableCell colSpan={2} className="text-center">
                    <If condition={hasPermission("create-employee")}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setActivating({
                            uuid: item?.user?.uuid,
                            name: item?.user?.name,
                          })
                        }
                      >
                        Ativar como colaborador
                      </Button>
                    </If>
                  </TableCell>
                ) : (
                  <>
                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={Boolean(item?.is_active)}
                    params={{
                      func: setStatusEmployees,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: values.is_active ? 0 : 1,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-employee")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-employee")}>
                      <DeleteBtn
                        params={{
                          func: deleteEmployeesData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.user?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-employee-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Colaborador" }}
                        className="sm:max-w-[720px]"
                        params={{
                          func: getShowEmployees,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <Tabs defaultValue="general" className="w-full">
                            <TabsList className="grid grid-cols-3 h-auto">
                              <TabsTrigger value="general">Geral</TabsTrigger>
                              <TabsTrigger value="personal">Pessoais</TabsTrigger>
                              <TabsTrigger value="documents">Documentos</TabsTrigger>
                              <TabsTrigger value="positions">Cargos</TabsTrigger>
                              <TabsTrigger value="salaries">Salários</TabsTrigger>
                              <TabsTrigger value="vacations">Férias</TabsTrigger>
                            </TabsList>

                            <div className="max-h-[60vh] overflow-y-auto pr-2 mt-3">
                              <TabsContent value="general" className="mt-0">
                                <div className="flex flex-col gap-4">
                                  <div className="rounded-lg border bg-primary/5 px-4 py-3">
                                    <div className="text-lg font-semibold leading-tight">
                                      {responseApi?.user?.name}
                                    </div>
                                    <div className="mt-0.5 text-sm text-muted-foreground break-all">
                                      {responseApi?.user?.email || "Email não informado"}
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      <Badge variant="secondary">
                                        {UserTypeLabel[responseApi?.user?.user_type_id] || "Somente ponto (RH)"}
                                      </Badge>
                                      {responseApi?.unity?.name && (
                                        <Badge variant="outline">{responseApi.unity.name}</Badge>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                      Organização
                                    </h4>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                      <InfoCell label="Setor" value={responseApi?.sector?.name} />
                                      <InfoCell label="Cargo" value={responseApi?.position?.name} />
                                      <InfoCell
                                        label="Horário de Trabalho"
                                        value={responseApi?.work_schedule?.name}
                                      />
                                    </div>
                                  </div>

                                  <div>
                                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                      Contrato
                                    </h4>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                      <InfoCell
                                        label="Data de Admissão"
                                        value={formatDate(responseApi?.admission_date)}
                                      />
                                      <InfoCell
                                        label="Desligamento"
                                        value={formatDate(responseApi?.termination_date)}
                                      />
                                    </div>
                                  </div>

                                  <div>
                                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                      Endereço
                                    </h4>
                                    <InfoCell
                                      label="Endereço completo"
                                      value={
                                        responseApi?.address?.street
                                          ? `${responseApi.address.street}, ${
                                              responseApi.address.number || "S/N"
                                            } - ${responseApi.address.neighborhood}, ${
                                              responseApi.address.city
                                            }/${responseApi.address.state}`
                                          : undefined
                                      }
                                    />
                                  </div>
                                </div>
                              </TabsContent>

                              <TabsContent value="personal" className="mt-0">
                                <div className="flex flex-col gap-4">
                                  <InfoSection title="Filiação" columns={2}>
                                    <InfoCell label="Nome do Pai" value={responseApi?.father_name} />
                                    <InfoCell label="Nome da Mãe" value={responseApi?.mother_name} />
                                  </InfoSection>

                                  <InfoSection title="Nascimento" columns={3}>
                                    <InfoCell
                                      label="Data de Nascimento"
                                      value={responseApi?.birth_date ? formatDate(responseApi.birth_date) : null}
                                    />
                                    <InfoCell
                                      label="Naturalidade"
                                      value={
                                        responseApi?.birthplace
                                          ? `${responseApi.birthplace}${
                                              responseApi.birthplace_state
                                                ? `/${responseApi.birthplace_state}`
                                                : ""
                                            }`
                                          : null
                                      }
                                    />
                                    <InfoCell label="Nacionalidade" value={responseApi?.nationality} />
                                  </InfoSection>

                                  <InfoSection title="Perfil" columns={2}>
                                    <InfoCell
                                      label="Sexo"
                                      value={responseApi?.gender ? GenderLabel[responseApi.gender] : null}
                                    />
                                    <InfoCell
                                      label="Estado Civil"
                                      value={
                                        responseApi?.marital_status
                                          ? MaritalStatusLabel[responseApi.marital_status]
                                          : null
                                      }
                                    />
                                    <InfoCell
                                      label="Etnia ou Raça"
                                      value={responseApi?.ethnicity ? EthnicityLabel[responseApi.ethnicity] : null}
                                    />
                                    <InfoCell
                                      label="Instrução"
                                      value={
                                        responseApi?.education_level
                                          ? EducationLevelLabel[responseApi.education_level]
                                          : null
                                      }
                                    />
                                  </InfoSection>

                                  <InfoSection title="Contato" columns={2}>
                                    <InfoCell label="Celular" value={responseApi?.mobile_phone} />
                                  </InfoSection>
                                </div>
                              </TabsContent>

                              <TabsContent value="documents" className="mt-0">
                                <div className="flex flex-col gap-4">
                                  <InfoSection title="Trabalhistas" columns={2}>
                                    <InfoCell
                                      label="CTPS"
                                      value={
                                        responseApi?.ctps_number
                                          ? `${responseApi.ctps_number} / ${responseApi.ctps_series || "-"} (${responseApi.ctps_state || "-"})`
                                          : null
                                      }
                                    />
                                    <InfoCell label="PIS/PASEP" value={responseApi?.pis_pasep} />
                                    <InfoCell
                                      label="Forma de Pagamento"
                                      value={
                                        responseApi?.payment_frequency
                                          ? PaymentFrequencyLabel[responseApi.payment_frequency]
                                          : null
                                      }
                                    />
                                  </InfoSection>

                                  <InfoSection title="Identificação" columns={2}>
                                    <InfoCell
                                      label="RG"
                                      value={
                                        responseApi?.rg_number
                                          ? `${responseApi.rg_number} - ${responseApi.rg_issuing_agency || "-"}/${responseApi.rg_state || "-"}`
                                          : null
                                      }
                                    />
                                    <InfoCell label="Habilitação" value={responseApi?.driver_license_number} />
                                  </InfoSection>
                                </div>
                              </TabsContent>

                              <TabsContent value="positions" className="mt-0">
                                <PositionHistorySection
                                  employeeId={responseApi?.id}
                                  unityId={responseApi?.unity_id}
                                />
                              </TabsContent>

                              <TabsContent value="salaries" className="mt-0">
                                <SalaryHistorySection employeeId={responseApi?.id} />
                              </TabsContent>

                              <TabsContent value="vacations" className="mt-0">
                                <VacationSection employeeId={responseApi?.id} />
                              </TabsContent>
                            </div>
                          </Tabs>
                        )}
                        loader={<CreateLoaderInput lines={4} />}
                      />
                    </If>
                  </div>
                </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={8}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
